// Disponibilidade por variante (Etapa 2): overrides e histórico persistem no MongoDB via API.
// Produto SEM variantes: o status pertence ao próprio produto (chave lógica PRODUCT_UNIT) —
// nenhuma variante fictícia é criada. Leitura pública (o site filtra o catálogo); escrita
// exige perfil interno (vendedor/admin) e SEM_PREVISAO exige admin (validado no servidor).
import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const EVENT = 'hud-availability-changed';
// Produto sem variantes: unidade comercial é o próprio produto (uso interno, nunca exibido).
export const PRODUCT_UNIT = '__produto__';

export const VARIANT_STATUS = {
  DISPONIVEL: 'DISPONIVEL',
  TEMPORARIAMENTE_INDISPONIVEL: 'TEMPORARIAMENTE_INDISPONIVEL',
  REVISAR: 'REVISAR',
  SEM_PREVISAO: 'SEM_PREVISAO',
};

export const VARIANT_STATUS_LABELS = {
  DISPONIVEL: 'Disponível',
  TEMPORARIAMENTE_INDISPONIVEL: 'Temporariamente indisponível',
  REVISAR: 'Revisar',
  SEM_PREVISAO: 'Sem previsão de reposição',
};

export const VARIANT_STATUS_SUBTITLES = {
  DISPONIVEL: null,
  TEMPORARIAMENTE_INDISPONIVEL: 'Este tecido/variante está temporariamente indisponível e não pode ser adicionado à sua solicitação agora.',
  REVISAR: 'Estamos revisando a disponibilidade deste tecido. Tente novamente em breve ou fale conosco.',
  SEM_PREVISAO: 'Este tecido/variante está sem previsão de reposição.',
};

let overridesCache = {};
let historyCache = [];
let ready = false;
let initPromise = null;

export const notifyAvailabilityChanged = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENT));
};

export function useAvailabilityTick() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const h = () => setTick((t) => t + 1);
    window.addEventListener(EVENT, h);
    return () => window.removeEventListener(EVENT, h);
  }, []);
  return tick;
}

const staffRole = () => {
  try {
    return JSON.parse(localStorage.getItem('hud_admin_user') || 'null')?.role || '';
  } catch {
    return '';
  }
};

// Hidrata overrides (público) e histórico (somente staff) a partir do servidor.
export function initAvailability(force = false) {
  if (initPromise && !force) return initPromise;
  initPromise = (async () => {
    try {
      const res = await fetch(`${API}/api/availability`);
      if (!res.ok) throw new Error('availability');
      const data = await res.json();
      overridesCache = data.overrides || {};
      ready = true;
      const role = staffRole();
      if (role === 'admin' || role === 'vendedor') {
        const hres = await fetch(`${API}/api/availability/history`, { headers: { 'X-Admin-Role': role } });
        if (hres.ok) historyCache = await hres.json();
      }
      notifyAvailabilityChanged();
    } catch (e) {
      console.error('Disponibilidade: API indisponível.', e);
    }
  })();
  return initPromise;
}

export const isAvailabilityReady = () => ready;

export const getOverrides = () => overridesCache;
export const getStatusHistory = () => historyCache;
export const pushHistory = (entry) => {
  historyCache = [...historyCache, entry];
};

const overrideKey = (product, variantId) =>
  `${product.slug}::${product.hasVariants === false ? PRODUCT_UNIT : variantId}`;

export function getVariantStatus(product, variantId) {
  if (!product) return VARIANT_STATUS.DISPONIVEL;
  if (product.hasVariants === false) {
    const ov = overridesCache[`${product.slug}::${PRODUCT_UNIT}`];
    return ov?.status || product.status || VARIANT_STATUS.DISPONIVEL;
  }
  const variant = product.variants.find((v) => v.id === variantId);
  if (!variant) return VARIANT_STATUS.DISPONIVEL;
  const ov = overridesCache[`${product.slug}::${variantId}`];
  return ov?.status || variant.status || VARIANT_STATUS.DISPONIVEL;
}

// Produto sem variantes: o próprio produto é a unidade comercial (nunca uma variante fictícia).
export const isVariantless = (product) => product?.hasVariants === false;

// Somente DISPONIVEL pode ser selecionada/adicionada a novas solicitações.
export function availableVariants(product) {
  if (isVariantless(product)) return [];
  return product.variants.filter(
    (v) => getVariantStatus(product, v.id) === VARIANT_STATUS.DISPONIVEL
  );
}

// Produto sem variantes é visível enquanto estiver DISPONIVEL (status no próprio produto).
// Produto com variantes some do catálogo público apenas se TODAS estiverem indisponíveis.
export function isProductVisible(product) {
  if (isVariantless(product)) return getVariantStatus(product, PRODUCT_UNIT) === VARIANT_STATUS.DISPONIVEL;
  return availableVariants(product).length > 0;
}

// SEM_PREVISAO é ação exclusiva de administrador (também validada no servidor).
export const canUserSetStatus = (user, status) =>
  status !== VARIANT_STATUS.SEM_PREVISAO || user?.role === 'admin';

export function setVariantStatus({ product, variantId, newStatus, user, origin = 'ATUALIZAR DISPONIBILIDADE', action }) {
  const variantless = product.hasVariants === false;
  const vid = variantless ? PRODUCT_UNIT : variantId;
  if (!variantless && !product.variants.some((v) => v.id === vid)) return { ok: false, reason: 'not-found' };
  if (!canUserSetStatus(user, newStatus)) return { ok: false, reason: 'sem-previsao-admin' };
  const key = `${product.slug}::${vid}`;
  const previous = getVariantStatus(product, vid);
  if (previous === newStatus) return { ok: false, reason: 'unchanged' };
  const variant = variantless ? null : product.variants.find((v) => v.id === vid);

  // Atualização otimista (UX preservada) + gravação no servidor (fonte única).
  const entry = { status: newStatus, changedAt: new Date().toISOString(), changedBy: user?.name || 'sistema' };
  const prevEntry = overridesCache[key];
  overridesCache = { ...overridesCache, [key]: entry };
  historyCache = [
    ...historyCache,
    {
      productSlug: product.slug,
      productName: product.name,
      variantId: vid,
      variantName: variant?.name || null,
      from: previous,
      to: newStatus,
      origin,
      action: action || 'Status alterado',
      userId: user?.id || null,
      userName: user?.name || null,
      at: entry.changedAt,
    },
  ];
  notifyAvailabilityChanged();

  fetch(`${API}/api/availability`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Role': staffRole() },
    body: JSON.stringify({
      productSlug: product.slug,
      variantId: vid,
      status: newStatus,
      user: { id: user?.id, name: user?.name },
      origin,
      action: action || 'Status alterado',
    }),
  })
    .then((r) => {
      if (!r.ok) throw new Error('availability');
    })
    .catch(() => {
      // Falha de gravação: reverte e ressincroniza com o servidor.
      overridesCache = { ...overridesCache };
      if (prevEntry) overridesCache[key] = prevEntry;
      else delete overridesCache[key];
      initAvailability(true);
    });
  return { ok: true };
}
